import { useEffect, useRef, useState } from 'react';

type Timer = { max: number; interval?: number; direction?: 'asc' | 'desc' };

export function useTimer({ max, interval = 1000, direction = 'desc' }: Timer) {
  const isDesc = direction === 'desc';
  const [time, setTime] = useState(isDesc ? max : 0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const maxRef = useRef(max);

  useEffect(() => {
    maxRef.current = max;
  }, [max]);

  const startTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      setTime(prevTime => {
        if (isDesc && prevTime > 0) {
          return prevTime - 1;
        } else if (!isDesc && prevTime < maxRef.current) {
          return prevTime + 1;
        }
        return prevTime;
      });
    }, interval);
  };

  const resetTimer = (nextMax?: number) => {
    clearTimer();
    const targetMax = typeof nextMax === 'number' ? nextMax : maxRef.current;
    setTime(isDesc ? targetMax : 0);
    startTimer();
  };

  const clearTimerRef = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const clearTimer = () => {
    clearTimerRef();
    setTime(0);
  };

  useEffect(() => {
    startTimer();

    return () => {
      clearTimerRef();
    };
  }, [interval, isDesc, max]);

  return {
    time,
    resetTimer,
    clearTimer,
  };
}
